import { BaseSystemModel, Guid, RecursivePartial, SM } from '@models/base';
import { WorkflowState } from './workflow-state';
import { WorkflowBrief } from './workflow-brief';

export class Workflow extends WorkflowBrief {
    _type = BaseSystemModel.Type.Workflow;

    states?: WorkflowState[];
    startStateId?: Guid;
    finishStateId?: Guid;

    _stateMap: SM<WorkflowState> = {};

    constructor(json?: RecursivePartial<Workflow>) {
        super(json);
        this.parseWorkflow(json || {});
    }

    parse(json: RecursivePartial<Workflow>): Workflow {
        super.parse(json);
        this.parseWorkflow(json);
        return this;
    }

    private parseWorkflow(json: RecursivePartial<Workflow>): void {
        Workflow.assignFields(this, json, [
            'startStateId',
            'finishStateId'
        ]);
        Workflow.assignClassArrays(this, json, { states: WorkflowState });
        this._stateMap = {};
        this.states?.forEach(st => this._stateMap[st.id!] = st);
    }
}

export const getBadWorkflowStates: (wf: Workflow) => { states: WorkflowState[], fromB: SM<boolean>, toA: SM<boolean> }  =
    wf => {
        if (!wf?.states?.length) {
            return { states: [], fromB: {}, toA: {} };
        }
        if (!wf.startStateId && !wf.finishStateId) {
            return {
                states: wf.states,
                fromB: wf.states.reduce((obj, st) => ({ ...obj, [st.id!]: true }), {}),
                toA: wf.states.reduce((obj, st) => ({ ...obj, [st.id!]: true }), {})
            }
        }
        const stMap: SM<WorkflowState> = wf.states.reduce((obj, st) => ({ ...obj, [st.id!]: st }), {});
        const fromB: SM<boolean> = {};
        const toA: SM<boolean> = {};
        const fb: SM<boolean> = { [wf.startStateId!]: true };
        const ta: SM<boolean> = { [wf.finishStateId!]: true };
        const states: WorkflowState[] = [];
        wf.states.forEach(st => {
            if (!hasPathFromBacklog(wf, st, stMap, fb)) {
                fromB[st.id!] = true;
            }
            if (!hasPathToArchive(wf, st, stMap, ta)) {
                toA[st.id!] = true;
            }
            if (fromB[st.id!] || toA[st.id!]) {
                states.push(st);
            }
        });
        return { states, fromB, toA };
    }

export const hasPathFromBacklog: (wf: Workflow, ws: WorkflowState, stMap: SM<WorkflowState>, checked: SM<boolean>, depth?: number) => boolean =
    (wf, ws, stMap, checked, depth) => {
        const states = Object.values(stMap);
        if (!ws?.id || !wf.startStateId || (depth && depth > states.length)) {
            return false;
        }
        if (checked[ws.id] != undefined) {
            return checked[ws.id];
        }
        for (const st of states) {
            if (st.id && st.id != ws.id && st.targets?.length && st.targets.findIndex(t => t.id == ws.id) != -1) {
                if (st.id == wf.startStateId) {
                    checked[st.id] = true;
                    return true;
                }
                if (checked[st.id] != undefined) {
                    return checked[st.id];
                }
                if (hasPathFromBacklog(wf, st, stMap, checked, (depth || 0) + 1)) {
                    checked[st.id] = true;
                    return true;
                }
            }
        }
        return false;
    }

export const hasPathToArchive: (wf: Workflow, ws: WorkflowState, stMap: SM<WorkflowState>, checked: SM<boolean>, depth?: number) => boolean =
    (wf, ws, stMap, checked, depth) => {
        if (!ws?.id || !wf.finishStateId || (depth && depth > Object.keys(stMap).length)) {
            return false;
        }
        if (checked[ws.id] != undefined) {
            return checked[ws.id];
        }
        if (ws.targets?.length) {
            for (const tr of ws.targets) {
                if (tr.id == wf.finishStateId) {
                    checked[ws.id] = true;
                    return true;
                }
                if (tr.id && stMap[tr.id]) {
                    if (checked[tr.id] != undefined) {
                        return checked[tr.id];
                    }
                    if (hasPathToArchive(wf, stMap[tr.id], stMap, checked, (depth || 0) + 1)) {
                        checked[ws.id] = true;
                        return true;
                    }
                }
                else {
                    return false;
                }
            }
            return false;
        }
        else {
            return false;
        }
    }
